import React from 'react';

interface AlgorithmInfoProps {
  algorithm: string;
  type: 'pathfinding' | 'sorting';
}

interface AlgorithmData {
  name: string;
  description: string;
  timeComplexity: string;
  spaceComplexity: string;
  features: string[];
}

type AlgorithmDescriptions = {
  pathfinding: {
    'Dijkstra': AlgorithmData;
    'A*': AlgorithmData;
  };
  sorting: {
    'Quick Sort': AlgorithmData;
    'Merge Sort': AlgorithmData;
    'Heap Sort': AlgorithmData;
    'Bubble Sort': AlgorithmData;
  };
};

const algorithmDescriptions: AlgorithmDescriptions = {
  pathfinding: {
    'Dijkstra': {
      name: "Dijkstra's Algorithm",
      description: "A greedy algorithm that finds the shortest path between nodes in a weighted graph. It guarantees the optimal solution by always exploring the closest unvisited node first.",
      timeComplexity: "O((V + E) log V)",
      spaceComplexity: "O(V)",
      features: [
        "Guarantees shortest path",
        "Works with weighted graphs",
        "Uses priority queue",
        "Explores nodes by distance"
      ]
    },
    'A*': {
      name: "A* Algorithm",
      description: "An informed search algorithm that uses heuristics to find the shortest path. It combines the cost-so-far (g) with an estimate of the cost-to-go (h) to make smarter decisions.",
      timeComplexity: "O(b^d)",
      spaceComplexity: "O(b^d)",
      features: [
        "Uses heuristics for efficiency",
        "Guarantees optimal solution",
        "Faster than Dijkstra",
        "Manhattan distance heuristic"
      ]
    }
  },
  sorting: {
    'Quick Sort': {
      name: "Quick Sort",
      description: "A divide-and-conquer algorithm that picks a 'pivot' element and partitions the array around it. It's one of the most efficient sorting algorithms in practice.",
      timeComplexity: "O(n log n) average, O(n²) worst",
      spaceComplexity: "O(log n)",
      features: [
        "In-place sorting",
        "Divide and conquer",
        "Unstable sort",
        "Good cache performance"
      ]
    },
    'Merge Sort': {
      name: "Merge Sort",
      description: "A stable divide-and-conquer algorithm that divides the array into halves, sorts them recursively, and then merges the sorted halves.",
      timeComplexity: "O(n log n)",
      spaceComplexity: "O(n)",
      features: [
        "Stable sorting",
        "Consistent performance",
        "Divide and conquer",
        "External sorting capable"
      ]
    },
    'Heap Sort': {
      name: "Heap Sort",
      description: "A comparison-based sorting algorithm that uses a binary heap data structure. It builds a max-heap and repeatedly extracts the maximum element.",
      timeComplexity: "O(n log n)",
      spaceComplexity: "O(1)",
      features: [
        "In-place sorting",
        "Unstable sort",
        "Consistent O(n log n)",
        "Uses heap data structure"
      ]
    },
    'Bubble Sort': {
      name: "Bubble Sort",
      description: "A simple comparison-based algorithm that repeatedly steps through the list, compares adjacent elements and swaps them if they are in the wrong order.",
      timeComplexity: "O(n²)",
      spaceComplexity: "O(1)",
      features: [
        "Simple to implement",
        "Stable sorting",
        "In-place sorting",
        "Good for small datasets"
      ]
    }
  }
};

export default function AlgorithmInfo({ algorithm, type }: AlgorithmInfoProps) {
  const typeDescriptions = algorithmDescriptions[type];
  const info = typeDescriptions[algorithm as keyof typeof typeDescriptions] as AlgorithmData | undefined;
  
  if (!info) return null;

  return (
    <div className="algorithm-info">
      <div className="algorithm-header">
        <h3>{info.name}</h3>
        <div className="complexity-badges">
          <span className="complexity-badge time">
            <strong>Time:</strong> {info.timeComplexity}
          </span>
          <span className="complexity-badge space">
            <strong>Space:</strong> {info.spaceComplexity}
          </span>
        </div>
      </div>
      
      <p className="algorithm-description">{info.description}</p>
      
      <div className="algorithm-features">
        <h4>Key Features:</h4>
        <ul>
          {info.features.map((feature, index) => (
            <li key={index}>{feature}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
